/**
 * Shows the console only to a signed-in superadmin. Everyone else gets a sign-in
 * card or a polite no. The backend enforces the same rule on every call; this
 * only decides what to render.
 */

import type { ReactNode } from "react";
import { ApiError } from "@/lib/api";
import { Button, focusRing } from "@/components/button";
import { Logo } from "@/components/display";
import { LoadError, Loading } from "@/components/states";
import { SIGN_IN_URL } from "./api";
import { useMe, useSignOut } from "./hooks";

export function AuthGate({ children }: { children: ReactNode }) {
  const { data: me, isPending, isError, error } = useMe();
  const signOut = useSignOut();

  if (isPending) return <Screen><Loading what="your account" /></Screen>;
  if (isError && error instanceof ApiError && error.status === 401) {
    return (
      <Screen>
        <Card title="Sign in to Ryuauth" body="Manage who can sign in to Qure's products, and what they can reach.">
          <a
            href={SIGN_IN_URL}
            className={`flex h-11 w-full items-center justify-center gap-3 rounded-lg bg-white text-sm font-medium text-zinc-900 shadow-xs ring-1 ring-zinc-300 hover:bg-zinc-50 dark:bg-zinc-800 dark:text-white dark:ring-zinc-700 dark:hover:bg-zinc-700 ${focusRing}`}
          >
            <MicrosoftMark />
            Sign in with Microsoft
          </a>
          <p className="text-xs text-zinc-500">Use your @qure.ai account.</p>
        </Card>
      </Screen>
    );
  }
  if (isError) return <Screen><LoadError what="your account" error={error} /></Screen>;
  if (me.org_role !== "superadmin") {
    return (
      <Screen>
        <Card
          title="This console is for superadmins"
          body={`You're signed in as ${me.email}. Ask a superadmin if you need to manage people, workspaces or services.`}
        >
          <Button className="w-full" onClick={() => signOut.mutate()}>Sign out</Button>
        </Card>
      </Screen>
    );
  }
  return <>{children}</>;
}

function Screen({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center px-4">{children}</div>;
}

function Card({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  return (
    <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-800">
      <Logo markOnly />
      <h1 className="mt-6 text-lg font-semibold tracking-tight text-zinc-950 dark:text-white">{title}</h1>
      <p className="mt-1.5 text-sm text-zinc-500 dark:text-zinc-400">{body}</p>
      <div className="mt-6 space-y-3 text-center">{children}</div>
    </div>
  );
}

function MicrosoftMark() {
  return (
    <svg aria-hidden width="16" height="16" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
